import { HttpClient } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { FeatureCodes } from '@piqet-angular/shared';
import { environment } from 'apps/piqet-angular/src/environments/environment';
import { BehaviorSubject } from 'rxjs';
import { CookieHelperService } from './cookie-helper.service';
export interface FeatureConfig {
  title: string;
  description: string;
  compactDescription?: string;
  showLearnMore?: boolean;
  popupSize?: 'large' | 'compact';
  learnMoreLink?: string;
}

@Injectable({
  providedIn: 'root',
})
export class FeatureConfigService {
  apiUrl = environment.url;
  private globalHidePromptSubject = new BehaviorSubject<boolean>(false);

  constructor(private http: HttpClient, private CookieHelperService: CookieHelperService) {
    this.initializeGlobalPreference();
  }

  private initializeGlobalPreference(): void {
    const globalPref = this.CookieHelperService.getGlobalUpgradePopupPreference();
    this.globalHidePromptSubject.next(globalPref === 'true');
  }

  private featureConfigs: Map<FeatureCodes, FeatureConfig> = new Map([
    // PROJECT features
    [
      FeatureCodes.PROJECT_ORG_PACKAGING_FOOTPRINT,
      {
        title: 'Premium Feature',
        description:
          'This advanced feature helps you track and analyze organizational packaging footprint across all products. Upgrade to unlock this capability.',
        compactDescription: 'Upgrade to access organizational footprint analysis',
        showLearnMore: true,
        popupSize: 'compact',
        learnMoreLink:
          'https://piqet.helpscoutdocs.com/article/14-what-is-the-organization-packaging-footprint',
      },
    ],

    [
      FeatureCodes.PRODUCT_PROJECT,
      {
        title: 'Premium Feature',
        description:
          'This advanced feature helps you track and analyze organizational packaging footprint across all products. Upgrade to unlock this capability.',
        compactDescription: 'Upgrade to access Product LCA project dashboard',
        showLearnMore: true,
        popupSize: 'large',
        learnMoreLink: 'https://piqet.helpscoutdocs.com/article/27-full-packaging-and-product-lca',
      },
    ],

    [
      FeatureCodes.PROJECT_SYSTEM_BOUNDARY_JUSTIFICATION,
      {
        title: 'Premium Feature',
        description:
          'Document and justify your LCA system boundaries with detailed explanations. This feature is available in premium plans.',
        compactDescription: 'Upgrade to Organizational or Enterprise license.',
        showLearnMore: true,
        popupSize: 'large',
      },
    ],

    [
      FeatureCodes.PROJECT_IMPACT_CATEGORY_JUSTIFICATION,
      {
        title: 'Premium Feature',
        description:
          'Provide detailed justifications for selected impact categories in your LCA studies. Available with premium access.',
        compactDescription: 'Upgrade to Organizational or Enterprise license.',
        showLearnMore: true,
        popupSize: 'large',
      },
    ],

    [
      FeatureCodes.PROJECT_OTHER_ASSUMPTIONS_LIMITATIONS,
      {
        title: 'Premium Feature',
        description:
          'Document assumptions and limitations for comprehensive LCA reporting. This premium feature enhances report transparency.',
        compactDescription: 'Upgrade to Organizational or Enterprise license.',
        showLearnMore: true,
        popupSize: 'large',
      },
    ],

    [
      FeatureCodes.PROJECT_SIMPLE_SCOPE_PRESET,
      {
        title: 'Premium Feature',
        description:
          'Access simplified scope presets to streamline your project setup. Available in premium plans.',
        compactDescription: 'Upgrade to use simple scope presets',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.PROJECT_RETAIL_SCOPE_PRESET,
      {
        title: 'Premium Feature',
        description:
          'Use retail-specific scope presets optimized for retail packaging assessments. Premium feature for advanced users.',
        compactDescription: 'Upgrade to access retail scope presets',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.PROJECT_FULL_PACKAGE_AND_PRODUCT_LCA,
      {
        title: 'Premium Feature',
        description:
          'Perform comprehensive package and product life cycle assessments. This advanced capability requires premium access.',
        compactDescription: 'Upgrade to access full package and product LCA',
        showLearnMore: true,
        popupSize: 'compact',
        learnMoreLink: 'https://piqet.helpscoutdocs.com/article/27-full-packaging-and-product-lca',
      },
    ],

    // SCENARIO features
    [
      FeatureCodes.SCENARIO_SCENARIO_INTERNAL_DATA,
      {
        title: 'Premium Feature',
        description:
          'Integrate internal data into your scenarios for more accurate assessments. Premium feature for enhanced analysis.',
        compactDescription: 'Upgrade to access organizational footprint analysis',
        showLearnMore: true,
        popupSize: 'compact',
        learnMoreLink:
          'https://piqet.helpscoutdocs.com/article/14-what-is-the-organization-packaging-footprint',
      },
    ],

    // MATERIAL features
    [
      FeatureCodes.MATERIAL_BOOKED_MARKED_COMPONENTS,
      {
        title: 'Premium Feature',
        description:
          'Track and manage bookmarked components for efficient material selection. Available with premium plans.',
        compactDescription: 'Upgrade to access bookmarked components',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.MATERIAL_MATERIAL_CERTIFICATION,
      {
        title: 'Premium Feature',
        description:
          'Add and track material certifications for compliance and reporting. Premium feature for certification management.',
        compactDescription: 'Upgrade to access material certification',
        showLearnMore: true,
        popupSize: 'compact',
        learnMoreLink: 'https://piqet.helpscoutdocs.com/article/23-material-certification',
      },
    ],

    [
      FeatureCodes.MATERIAL_CUSTOM_TRANSPORT,
      {
        title: 'Premium Feature',
        description:
          'Define custom transport scenarios for accurate material logistics modeling. Available in premium plans.',
        compactDescription: 'Upgrade to create custom transport scenarios',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.MATERIAL_CUSTOM_CONVERSION,
      {
        title: 'Premium Feature',
        description:
          'Create custom conversion processes for specialized material transformations. Premium capability for advanced modeling.',
        compactDescription: 'Upgrade to define custom conversions',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.MATERIAL_CUSTOM_MATERIAL,
      {
        title: 'Premium Feature',
        description:
          'Define custom materials with specific environmental profiles. This premium feature enables precise material modeling.',
        compactDescription: 'Upgrade to create custom materials',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    // PRODUCT features
    [
      FeatureCodes.PRODUCT_PRODUCT_SECTOR_DATA,
      {
        title: 'Premium Feature',
        description:
          'Access sector-specific product data for industry benchmarking. Premium feature for competitive analysis.',
        compactDescription: 'Upgrade to access sector data',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.PRODUCT_FULL_PACKAGE_AND_PRODUCT_LCA,
      {
        title: 'Premium Feature',
        description:
          'Conduct comprehensive product and packaging life cycle assessments. Advanced premium capability.',
        compactDescription: 'Upgrade for full product LCA capabilities',
        showLearnMore: true,
        popupSize: 'compact',
        learnMoreLink: 'https://piqet.helpscoutdocs.com/article/27-full-packaging-and-product-lca',
      },
    ],

    // FILLING features
    [
      FeatureCodes.FILLING_CUSTOM_FILLING,
      {
        title: 'Premium Feature',
        description:
          'Define custom filling processes for unique product requirements. Available with premium access.',
        compactDescription: 'Upgrade to create custom filling processes',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    // EOL features
    [
      FeatureCodes.EOL_CUSTOM_END_OF_LIFE,
      {
        title: 'Premium Feature',
        description:
          'Model custom end-of-life scenarios for comprehensive waste management analysis. Premium feature required.',
        compactDescription: 'Upgrade for custom EOL scenarios',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    // REPORTS features
    [
      FeatureCodes.REPORTS_CHART_TYPE,
      {
        title: 'Premium Feature',
        description:
          'Access advanced chart types for enhanced data visualization. Premium feature for professional reporting.',
        compactDescription: 'Upgrade to access advanced charts',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_LIFE_CYCLE_STAGE,
      {
        title: 'Premium Feature',
        description:
          'Generate detailed life cycle stage reports for comprehensive analysis. Available in premium plans.',
        compactDescription: 'Upgrade for life cycle stage reporting',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_PACKAGING_LEVEL,
      {
        title: 'Premium Feature',
        description:
          'Create packaging level reports for multi-tier packaging systems. Premium reporting capability.',
        compactDescription: 'Upgrade for packaging level reports',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_PACKAGING_COMPONENTS,
      {
        title: 'Premium Feature',
        description:
          'Generate detailed component-level packaging reports. This premium feature enables granular analysis.',
        compactDescription: 'Upgrade for component-level reporting',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_PRODUCT_LOSS,
      {
        title: 'Premium Feature',
        description:
          'Analyze product loss throughout the supply chain. Premium feature for waste reduction insights.',
        compactDescription: 'Upgrade to analyze product loss',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_INGREDIENTS,
      {
        title: 'Premium Feature',
        description:
          'Generate ingredient-level impact reports for detailed product analysis. Available with premium access.',
        compactDescription: 'Upgrade for ingredient reporting',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_TRANSPORT,
      {
        title: 'Premium Feature',
        description:
          'Create comprehensive transport impact reports across your supply chain. Premium reporting feature.',
        compactDescription: 'Upgrade for transport impact reports',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_PEF_CATEGORIES_WITH_TOXICITY,
      {
        title: 'Premium Feature',
        description:
          'Access PEF categories including toxicity indicators for complete environmental assessment. Premium feature.',
        compactDescription: 'Upgrade for PEF with toxicity data',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_PEF_CATEGORIES_WITHOUT_TOXICITY,
      {
        title: 'Premium Feature',
        description:
          'Generate PEF category reports for standard environmental impact assessment. Available in premium plans.',
        compactDescription: 'Upgrade for PEF category reports',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_GLOBAL_PACKAGING_PROTOCOL_METRICS,
      {
        title: 'Premium Feature',
        description:
          'Report on Global Packaging Protocol metrics for industry compliance. Premium feature for standardized reporting.',
        compactDescription: 'Upgrade for GPP metric reporting',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_ALL_OTHER_METRICS,
      {
        title: 'Premium Feature',
        description:
          'Access comprehensive metric reporting beyond standard indicators. Premium capability for advanced analysis.',
        compactDescription: 'Upgrade for advanced metric reporting',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_INTERPRETATION_SECTION,
      {
        title: 'Premium Feature',
        description:
          'Add professional interpretation sections to your reports. This premium feature enhances report quality.',
        compactDescription: 'Upgrade to access ISO 14044 compliant report features',
        showLearnMore: true,
        popupSize: 'compact',
        learnMoreLink:
          'https://piqet.helpscoutdocs.com/article/25-iso-14044-compliant-reporting-in-piqet ',
      },
    ],

    [
      FeatureCodes.REPORTS_RAW_DATA_EXPORT,
      {
        title: 'Premium Feature',
        description:
          'Export raw data for external analysis and processing. Premium feature for data portability.',
        compactDescription: 'Upgrade to export raw data',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_LCA_REPORT,
      {
        title: 'Premium Feature',
        description:
          'Generate comprehensive LCA reports with all supporting documentation. Available with premium access.',
        compactDescription: 'Upgrade for full LCA reporting',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_ISO_14044_LCA_REPORT,
      {
        title: 'Premium Feature',
        description:
          'Create ISO 14044 compliant LCA reports for regulatory and certification requirements. Premium feature.',
        compactDescription: 'Upgrade for ISO-compliant reports',
        showLearnMore: true,
        popupSize: 'compact',
        learnMoreLink:
          'https://piqet.helpscoutdocs.com/article/25-iso-14044-compliant-reporting-in-piqet',
      },
    ],

    [
      FeatureCodes.REPORTS_CUSTOM_REPORT,
      {
        title: 'Premium Feature',
        description:
          'Design custom reports tailored to your specific requirements. Premium capability for flexible reporting.',
        compactDescription: 'Upgrade to create custom reports',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.REPORTS_SENSITIVITY_ANALYSIS,
      {
        title: 'Premium Feature',
        description:
          'Perform sensitivity analysis to understand result variability. Advanced premium feature for robust analysis.',
        compactDescription: 'Upgrade to access advanced reporting features',
        showLearnMore: true,
        popupSize: 'large',
      },
    ],

    // CUSTOM features
    [
      FeatureCodes.CUSTOM_MATERIAL,
      {
        title: 'Premium Feature',
        description:
          'Create and manage custom materials with specific properties. Premium feature for material innovation.',
        compactDescription: 'Upgrade to define custom materials',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.CUSTOM_CONVERSION,
      {
        title: 'Premium Feature',
        description:
          'Define custom conversion processes for unique manufacturing needs. Available with premium access.',
        compactDescription: 'Upgrade for custom conversions',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.CUSTOM_TRUCKS,
      {
        title: 'Premium Feature',
        description:
          'Model custom truck specifications for accurate transport assessment. Premium transport modeling feature.',
        compactDescription: 'Upgrade to define custom trucks',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.CUSTOM_TRANSPORT_CHAINS,
      {
        title: 'Premium Feature',
        description:
          'Create complex multi-modal transport chains. This premium feature enables sophisticated logistics modeling.',
        compactDescription: 'Upgrade for custom transport chains',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.CUSTOM_FILLING,
      {
        title: 'Premium Feature',
        description:
          'Define custom filling processes for specialized production lines. Premium manufacturing feature.',
        compactDescription: 'Upgrade for custom filling processes',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.CUSTOM_STORAGE,
      {
        title: 'Premium Feature',
        description:
          'Model custom storage conditions and their environmental impacts. Available in premium plans.',
        compactDescription: 'Upgrade to model custom storage',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.CUSTOM_REUSE,
      {
        title: 'Premium Feature',
        description:
          'Create custom reuse scenarios for circular economy modeling. Premium sustainability feature.',
        compactDescription: 'Upgrade for custom reuse scenarios',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.CUSTOM_WASTE_SCENARIOS,
      {
        title: 'Premium Feature',
        description:
          'Define custom waste management scenarios for comprehensive EOL analysis. Premium feature required.',
        compactDescription: 'Upgrade for custom waste scenarios',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    // PREFERENCES features
    [
      FeatureCodes.PREFERENCES_LIST_OF_SAVED_COMPONENTS,
      {
        title: 'Premium Feature',
        description:
          'Save and manage frequently used components for efficient workflow. Available with premium access.',
        compactDescription: 'Upgrade to save component lists',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.PREFERENCES_LIST_OF_SUPPLIERS,
      {
        title: 'Premium Feature',
        description:
          'Maintain a comprehensive supplier database for supply chain management. Premium feature for procurement.',
        compactDescription: 'Upgrade to manage supplier lists',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.PREFERENCES_MATERIAL_CERTIFICATIONS,
      {
        title: 'Premium Feature',
        description:
          'Track and manage material certifications across your organization. Premium compliance feature.',
        compactDescription: 'Upgrade to access material certification',
        showLearnMore: true,
        popupSize: 'compact',
        learnMoreLink: 'https://piqet.helpscoutdocs.com/article/23-material-certification',
      },
    ],

    [
      FeatureCodes.PREFERENCES_CUSTOM_INDICATORS,
      {
        title: 'Premium Feature',
        description:
          'Create custom environmental indicators tailored to your needs. Available in premium plans.',
        compactDescription: 'Upgrade to create custom indicators',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.PREFERENCES_LITTER_IMPACT,
      {
        title: 'Premium Feature',
        description:
          'Configure litter impact assessment methodologies. Premium feature for marine litter analysis.',
        compactDescription: 'Upgrade to Organizational or Enterprise license.',
        showLearnMore: true,
        popupSize: 'large',
      },
    ],

    [
      FeatureCodes.PREFERENCES_RECYCLED_CONTENT,
      {
        title: 'Premium Feature',
        description:
          'Set recycled content preferences and targets across your portfolio. Premium sustainability feature.',
        compactDescription: 'Upgrade for recycled content settings',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.PREFERENCES_ORGANIZATION_WEIGHTINGS,
      {
        title: 'Premium Feature',
        description:
          'Define organization-specific impact weightings for customized assessments. Premium feature required.',
        compactDescription: 'Upgrade to access advanced modelling features ',
        showLearnMore: true,
        popupSize: 'large',
      },
    ],

    [
      FeatureCodes.PREFERENCES_MANAGE_USERS_TEAMS_LOGIN,
      {
        title: 'Premium Feature',
        description:
          'Manage users, teams, and access permissions across your organization. Premium administrative feature.',
        compactDescription: 'Upgrade for user management',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.PREFERENCES_PROJECT_WORKFLOW,
      {
        title: 'Premium Feature',
        description:
          'Customize project workflows to match your organizational processes. Available with premium access.',
        compactDescription: 'Upgrade for custom workflows',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.PREFERENCES_SCENARIO_WORKFLOW,
      {
        title: 'Premium Feature',
        description:
          'Define scenario comparison workflows for systematic analysis. Premium workflow management feature.',
        compactDescription: 'Upgrade for scenario workflows',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    // ADVANCED features
    [
      FeatureCodes.ADVANCED_ADD_INTERNAL_DATA,
      {
        title: 'Premium Feature',
        description:
          'Integrate internal data sources for enhanced accuracy. This premium feature enables proprietary data usage.',
        compactDescription: 'Upgrade to access organizational footprint analysis',
        showLearnMore: true,
        popupSize: 'compact',
        learnMoreLink:
          'https://piqet.helpscoutdocs.com/article/14-what-is-the-organization-packaging-footprint',
      },
    ],

    [
      FeatureCodes.ADVANCED_ORG_PACKAGING_FOOTPRINT_REPORT,
      {
        title: 'Premium Feature',
        description:
          'Generate organization-wide packaging footprint reports. Premium feature for corporate sustainability reporting.',
        compactDescription: 'Upgrade to Organizational or Enterprise license.',
        showLearnMore: true,
        popupSize: 'compact',
        learnMoreLink:
          'https://piqet.helpscoutdocs.com/article/14-what-is-the-organization-packaging-footprint',
      },
    ],

    [
      FeatureCodes.ADVANCED_EXPORT_LOGS,
      {
        title: 'Premium Feature',
        description:
          'Export detailed activity logs for audit and compliance purposes. Available in premium plans.',
        compactDescription: 'Upgrade to export logs',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.CUSTOM_MATERIAL_CO2_EF_VALUE,
      {
        title: 'Premium Feature',
        description:
          'Custom material CO2 EF value. Available in premium plans.',
        compactDescription: 'Upgrade to custom material CO2 EF value',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.ADVANCED_IMPORT,
      {
        title: 'Premium Feature',
        description:
          'Import data from various formats for efficient project setup. Premium data management feature.',
        compactDescription: 'Upgrade to access advanced feature',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.ADVANCED_PROJECT_REPORT_SETTINGS,
      {
        title: 'Premium Feature',
        description:
          'Configure advanced report settings for professional documentation. Premium reporting capability.',
        compactDescription: 'Upgrade to access advanced reporting feature',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    // HELP features
    [
      FeatureCodes.HELP_BASIC_GUIDED_TOUR,
      {
        title: 'Premium Feature',
        description:
          'Access guided tours for basic platform features. Available with all premium plans.',
        compactDescription: 'Upgrade for guided tours',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],

    [
      FeatureCodes.HELP_ADVANCED_GUIDED_TOUR,
      {
        title: 'Premium Feature',
        description:
          'Get advanced guided tours for complex features and workflows. Premium training resource.',
        compactDescription: 'Upgrade to Organizational or Enterprise license.',
        showLearnMore: true,
        popupSize: 'compact',
      },
    ],
  ]);

  getFeatureConfig(featureCode: FeatureCodes): FeatureConfig {
    return this.featureConfigs.get(featureCode) || this.getDefaultConfig();
  }

  private getDefaultConfig(): FeatureConfig {
    return {
      title: 'Premium Feature',
      description:
        'This advanced feature is available with upgraded plans. Unlock powerful capabilities to enhance your sustainability analysis.',
      compactDescription: 'Upgrade to access this feature',
      showLearnMore: true,
      popupSize: 'compact',
    };
  }

  saveUserPreference(preferenceType: any, isEnabled: any) {
    return this.http
      .get<any>(
        `${this.apiUrl}/UserPreferences/SaveUserPreference?preferenceType=${preferenceType}&isEnabled=${isEnabled}`
      )
      .toPromise();
  }

  getGlobalHidePromptObservable() {
    return this.globalHidePromptSubject.asObservable();
  }

  setHidePromptGlobally(hide: boolean): void {
    this.globalHidePromptSubject.next(hide);
  }

  handleHideFeatureUpgrade(hideFeatureUpgrade: boolean | undefined | null): void {
    if (hideFeatureUpgrade !== undefined && hideFeatureUpgrade !== null) {
      const oneYearFromNow = new Date(new Date().getTime() + 365 * 24 * 60 * 60 * 1000);
      this.CookieHelperService.setGlobalUpgradePopupPreference(hideFeatureUpgrade, oneYearFromNow);
      this.setHidePromptGlobally(hideFeatureUpgrade);
    } else {
    }
  }
}
